import React, { useCallback, useContext, useState } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';

import VerticalDots from 'mdi-react/DotsVerticalIcon';

import { ParamsAndReturnTypes } from '@prisjakt/ui-translate/src/translate-func';
import Dropdown from '@prisjakt/ui-dropdown-new';

import GoToStoreButton from '../go-to-store-button';
import PriceColumn from '../price-column';
import CampaignBadge from '../../../campaign-badge';
import ReportModal from '../report-button/report-modal';

import { AlternativePrice, Campaign, Price } from '../../../../types';
import { TableLinkProps } from '../../types';
import { SortOption } from '../../../../areas/product/components/sort-options/types';
import { CurrentCampaign } from '../../../../areas/product/types';

import * as Table from '../../styles/table';
import * as S from './styles';

type OwnProps = {
  campaign?: CurrentCampaign | null;
  externalUri?: string | null;
  onClickOut: (e: React.MouseEvent, id?: string | null) => void;
  isInternationalPrice: boolean;
  price: AlternativePrice;
  priceStore?: Price['store'];
  productImage?: string | null;
  tableLinkProps: TableLinkProps;
  currentSort: SortOption;
};

const AlternativePriceRow = ({
  campaign,
  externalUri,
  onClickOut,
  isInternationalPrice,
  price,
  priceStore,
  productImage,
  tableLinkProps,
  currentSort,
}: OwnProps) => {
  const { t } = useContext(AppContext);

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const featured = priceStore && priceStore.featured;

  const openModal = useCallback(
    (e: React.MouseEvent) => {
      // Stops the PriceRow link and onClick from firing
      e.preventDefault();
      e.stopPropagation();

      setModalIsOpen(true);
    },
    [setModalIsOpen]
  );

  const closeModal = useCallback(() => setModalIsOpen(false), [setModalIsOpen]);

  const handleClickOut = useCallback(
    (e: React.MouseEvent) => onClickOut(e, price.id),
    [price.id, onClickOut]
  );

  const renderProductNameColumn = () => (
    <Table.Column colSpan={2} expandOnMobile>
      <Table.Link
        {...tableLinkProps}
        onClick={handleClickOut}
        href={price.externalUri!}
        vertical
        alignLeft
      >
        <div>
          <S.ProductName>{price.name}</S.ProductName>
          <S.StockStatus>
            {price && price.stock && price.stock.statusText
              ? price.stock.statusText.replace(/-/g, '\u2011')
              : t(
                  'product',
                  `stock-status.${(price.stock && price.stock.status) ||
                    'unknown'}` as keyof ParamsAndReturnTypes['product']
                )}
          </S.StockStatus>
        </div>
      </Table.Link>
    </Table.Column>
  );

  const renderStockStatusColumn = () => (
    <Table.Column>
      <Table.Link
        {...tableLinkProps}
        onClick={handleClickOut}
        href={price.externalUri!}
        alignCenter
      >
        <S.StockStatusColumn
          status={
            price.stock && price.stock.status ? price.stock.status : undefined
          }
          statusText={
            price.stock && price.stock.statusText
              ? price.stock.statusText
              : undefined
          }
          countryCode={priceStore && priceStore.countryCode}
          toneDown={!featured}
          isInternationalPrice={isInternationalPrice}
        />
      </Table.Link>
    </Table.Column>
  );

  const renderPriceColumn = () => (
    <Table.Column>
      <Table.Link
        {...tableLinkProps}
        onClick={handleClickOut}
        href={price.externalUri!}
        alignRight
      >
        {price.price && (
          <S.PriceColumnWrapper>
            {campaign && (
              <CampaignBadge
                campaign={
                  { ...campaign, id: parseInt(campaign.id, 10) } as Campaign
                }
              />
            )}
            <PriceColumn
              price={price.price}
              storeIsFeatured={featured}
              isCampaign={Boolean(campaign)}
              align="right"
              currentSort={currentSort}
            />
          </S.PriceColumnWrapper>
        )}
      </Table.Link>
    </Table.Column>
  );

  const renderGoToStoreButton = () => (
    <Table.Column>
      <Table.Link
        {...tableLinkProps}
        onClick={handleClickOut}
        href={price.externalUri!}
        alignRight
      >
        <GoToStoreButton
          visible={Boolean(externalUri || featured)}
          disabled={Boolean(externalUri && !featured)}
          featured={Boolean(featured)}
        />
      </Table.Link>
    </Table.Column>
  );

  const renderOptionsButton = () => (
    <Table.Column>
      <ReportModal
        isOpen={modalIsOpen}
        closeModal={closeModal}
        priceId={price.id}
        priceValue={price.price && price.price.exclShipping}
        priceStore={priceStore}
        productImage={productImage}
        productName={price.name}
        stockStatus={price.stock && price.stock.status}
      />

      <S.Options>
        <Dropdown
          variant="secondary"
          showArrow={false}
          toggleElement={
            <S.OptionsButton>
              <VerticalDots />
            </S.OptionsButton>
          }
          grow="right"
        >
          <S.ReportOverlay>
            <S.Option type="button" onClick={openModal}>
              {t('feedback', 'dialog.prompt-button')}
            </S.Option>
          </S.ReportOverlay>
        </Dropdown>
      </S.Options>
    </Table.Column>
  );

  return (
    <Table.AltRow>
      {renderOptionsButton()}
      {renderProductNameColumn()}
      {renderStockStatusColumn()}
      {renderPriceColumn()}
      {renderGoToStoreButton()}
    </Table.AltRow>
  );
};

export default AlternativePriceRow;
